/**
 * Scattered parts that settle in, then become draggable.
 *
 * Entrance: each piece drifts in from further out, staggered, and is only
 * armed for dragging once its own transition has finished — so a piece cannot
 * be grabbed mid-flight and fight its own animation.
 *
 * Dragging: Pointer Events, so mouse, touch and pen are one code path. The
 * piece keeps whatever position it is dropped at; nothing snaps back, because
 * the point of it is that the parts are yours to arrange.
 *
 * Position is held as a translate offset in pixels on top of the layout
 * position the merchant set in percentages. Keeping the two separate means a
 * dragged piece still reflows sensibly when the viewport resizes.
 *
 * Under prefers-reduced-motion there is no entrance and no transition; pieces
 * are simply present, and remain draggable.
 */
class NoriScatter extends HTMLElement {
  /** @type {HTMLElement[]} */
  #pieces = [];
  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {Map<HTMLElement, {x: number, y: number}>} */
  #offsets = new Map();
  /** @type {{piece: HTMLElement, pointerId: number, startX: number, startY: number, originX: number, originY: number} | null} */
  #drag = null;
  #reduced = false;

  connectedCallback() {
    this.#pieces = [...this.querySelectorAll('[data-scatter-piece]')];
    if (this.#pieces.length === 0) return;

    this.#reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

    for (const piece of this.#pieces) this.#offsets.set(piece, { x: 0, y: 0 });

    if (this.#reduced) {
      this.#arm();
      return;
    }

    // Held back until the section is actually looked at, so the entrance is
    // not already over by the time it scrolls into view.
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#enter();
      },
      { threshold: 0.25 },
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.removeEventListener('pointerdown', this.#onPointerDown);
  }

  #enter() {
    const stagger = Number.parseFloat(this.dataset.stagger ?? '') || 90;

    this.#pieces.forEach((piece, index) => {
      piece.style.transitionDelay = `${index * stagger}ms`;
    });

    // Next frame, so the browser has painted the pre-entrance state and the
    // change actually transitions instead of jumping.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        this.setAttribute('data-entered', '');

        const last = this.#pieces[this.#pieces.length - 1];
        const done = (event) => {
          if (event.target !== last || event.propertyName !== 'transform') return;
          last.removeEventListener('transitionend', done);
          this.#arm();
        };
        last.addEventListener('transitionend', done);
        // transitionend does not fire if the transition is interrupted or the
        // tab is backgrounded, so arm on a timer as well.
        const total = this.#pieces.length * stagger + 1200;
        setTimeout(() => this.#arm(), total);
      }),
    );
  }

  #arm() {
    if (this.hasAttribute('data-armed')) return;
    this.setAttribute('data-armed', '');
    this.setAttribute('data-entered', '');
    for (const piece of this.#pieces) piece.style.transitionDelay = '';
    this.addEventListener('pointerdown', this.#onPointerDown);
  }

  #onPointerDown = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const piece = target.closest('[data-scatter-piece]');
    if (!(piece instanceof HTMLElement) || !this.contains(piece)) return;

    event.preventDefault();

    const offset = this.#offsets.get(piece) ?? { x: 0, y: 0 };
    this.#drag = {
      piece,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: offset.x,
      originY: offset.y,
    };

    piece.setPointerCapture(event.pointerId);
    piece.dataset.dragging = '';
    // Lifted above its neighbours and kept there, so the piece you last moved
    // stays on top rather than sliding back under the others.
    piece.style.zIndex = String(++NoriScatter.topLayer);

    this.addEventListener('pointermove', this.#onPointerMove);
    this.addEventListener('pointerup', this.#onPointerUp);
    this.addEventListener('pointercancel', this.#onPointerUp);
  };

  #onPointerMove = (event) => {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    let x = drag.originX + (event.clientX - drag.startX);
    let y = drag.originY + (event.clientY - drag.startY);

    // Clamped so a piece cannot be dragged out of the section and lost.
    //
    // Measured with offsetLeft/offsetWidth rather than getBoundingClientRect:
    // every piece carries a rotation, and a rect describes the *rotated*
    // bounding box — inflated, and offset from the element's own origin. The
    // translate being clamped here is applied at that origin, so the limits
    // have to come from layout geometry, which ignores transforms.
    const { offsetLeft: restLeft, offsetTop: restTop } = drag.piece;
    const minX = -restLeft;
    const maxX = this.clientWidth - drag.piece.offsetWidth - restLeft;
    const minY = -restTop;
    const maxY = this.clientHeight - drag.piece.offsetHeight - restTop;

    x = Math.min(Math.max(x, minX), maxX);
    y = Math.min(Math.max(y, minY), maxY);

    this.#offsets.set(drag.piece, { x, y });
    drag.piece.style.setProperty('--nori-scatter-dx', `${x}px`);
    drag.piece.style.setProperty('--nori-scatter-dy', `${y}px`);
  };

  #onPointerUp = (event) => {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    try {
      drag.piece.releasePointerCapture(drag.pointerId);
    } catch {
      // The pointer can already be gone; nothing to release.
    }
    delete drag.piece.dataset.dragging;
    this.#drag = null;

    this.removeEventListener('pointermove', this.#onPointerMove);
    this.removeEventListener('pointerup', this.#onPointerUp);
    this.removeEventListener('pointercancel', this.#onPointerUp);
  };

  static topLayer = 1;
}

if (!customElements.get('nori-scatter')) {
  customElements.define('nori-scatter', NoriScatter);
}
